import { useDispatch } from "react-redux";
import { UnitTable } from "./components/units/UnitTable";
import { UnitTableControls } from "./components/controls/UnitTableControls";
import { useEffect } from "react";
import { setApiKey } from "./features/api/apiKeySlice";
import {
    DisplayOptionContext,
    defaultDisplayOption,
} from "@/context/DisplayOptionContext";
import { validateDisplayOptions } from "./lib/validations";
import type { SeVaporAppProps, UnitGroup } from "./types";
import {
    GetUnitGroupsParams,
    useGetUnitGroupsQuery,
} from "./features/api/apiSlice";
import merge from "lodash/merge";
import { UnitJourneyWrapper } from "./components/journey/UnitJourneyWrapper";
import { LocationFilterToast } from "./components/controls/filter/LocationFilterToast";
import { AnnexLocationsMessage } from "./components/facility/AnnexLocationsMessage";

const App = ({ apiKey, options }: SeVaporAppProps) => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setApiKey(apiKey));
    }, [apiKey, dispatch]);

    const mergedOptions = merge({}, defaultDisplayOption, options);

    try {
        validateDisplayOptions(mergedOptions);
    } catch (error) {
        if (error instanceof Error) {
            console.error(error.message);
        } else {
            console.error("An unknown error occurred.");
        }
    }

    // Build query params - only include defined values
    const unitParams: GetUnitGroupsParams = {
        "facility-ids": mergedOptions.facilityIds.join(","),
    };

    if (mergedOptions.additionalUnitSourceInfo) {
        unitParams["unit-source"] = mergedOptions.additionalUnitSourceInfo;
    }
    if (mergedOptions.allowedFeatures) {
        unitParams.features = mergedOptions.allowedFeatures;
    }
    if (mergedOptions.allowedCategories) {
        unitParams.categories = mergedOptions.allowedCategories;
    }
    if (mergedOptions.sort) {
        unitParams.sort = mergedOptions.sort;
    }
    if (mergedOptions.groupBy) {
        unitParams.group = mergedOptions.groupBy;
    }

    const {
        data: unitGroupsData,
        error: unitGroupsError,
        isLoading: unitGroupsLoading,
    } = useGetUnitGroupsQuery(unitParams, { skip: !apiKey });

    // Extract unique features from unitGroupsData
    const availableFeatures = Array.from(
        new Map(
            unitGroupsData?.data
                ?.flatMap(
                    (unitGroup: UnitGroup) =>
                        unitGroup.representative_unit.features,
                )
                .map((feature) => [feature.id, feature]) ?? [],
        ).values(),
    );

    const availableCategories = Array.from(
        new Map(
            unitGroupsData?.data
                ?.filter(
                    (unitGroup: UnitGroup) =>
                        unitGroup.representative_unit.category !== null,
                )
                .map((unitGroup: UnitGroup) => [
                    unitGroup.representative_unit.category.id,
                    unitGroup.representative_unit.category,
                ]) ?? [],
        ).values(),
    );
    return (
        <DisplayOptionContext.Provider value={mergedOptions}>
            <UnitTableControls
                features={availableFeatures}
                categories={availableCategories}
            />
            <AnnexLocationsMessage unitGroups={unitGroupsData?.data ?? []} />
            <UnitTable
                units={unitGroupsData?.data ?? []}
                unitsError={unitGroupsError}
                unitsIsLoading={unitGroupsLoading}
            />
            <UnitJourneyWrapper />
            <LocationFilterToast />
        </DisplayOptionContext.Provider>
    );
};

export default App;
